import { resolveExecutor, getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import { getCustomerConfig } from '@hopdrive/sdk-configs'
import type { Routing } from '@hopdrive/sdk-configs/definitions'
import { getDefaultGeoProviders } from '../../init'
import { getLane } from '../lane-lookup/getLane'
import { updateLaneEnrichment } from './updateLaneEnrichment'
import { updateLaneStatus } from '../lane-update/updateLaneStatus'
import { LaneNotFoundError, LaneError } from '../../errors/lane-errors'
import type { LaneWithLocations, LaneCreationCriteria, ConfigSource } from '../../types'
import type { ComputeRouteRequest, RoutePreference } from '../../geo/types/routing'
import type {
  EnrichLaneInput,
  EnrichLaneResult,
  RoutingOptions,
  RouteMetadata,
} from './types'
import { generateOperationId, scopeLogger, elapsed } from '../../logging'

// Conversion constant
const METERS_TO_MILES = 0.000621371

// Duration constants
const PICKUP_DWELL_TIME = 300
const PICKUP_INSPECTION_TIME = 300
const DELIVERY_DWELL_TIME = 300
const DELIVERY_INSPECTION_TIME = 180

const PICKUP_INSPECTION_SEC = PICKUP_DWELL_TIME + PICKUP_INSPECTION_TIME     // 600
const DELIVERY_INSPECTION_SEC = DELIVERY_DWELL_TIME + DELIVERY_INSPECTION_TIME // 480

/**
 * Map lowercase route preference to provider format
 */
function toRoutePreference(pref?: 'fastest' | 'shortest'): RoutePreference | undefined {
  if (!pref) return undefined
  return pref.toUpperCase() as RoutePreference
}

/**
 * Convert toll amount from cents to dollars
 */
function tollCentsToDollars(amountCents: number | undefined): number | null {
  if (amountCents === undefined || amountCents === null) return null
  return Math.round(amountCents) / 100
}

/**
 * Convert meters to miles
 */
function metersToMiles(meters: number): number {
  return Math.round(meters * METERS_TO_MILES * 100) / 100
}

/**
 * Resolved routing options with their source attribution
 */
interface ResolvedRoutingOptions {
  avoidTolls: boolean
  avoidTollsSource: ConfigSource
  avoidHighways: boolean
  avoidHighwaysSource: ConfigSource
  avoidFerries: boolean
  avoidFerriesSource: ConfigSource
  routePreference: 'fastest' | 'shortest'
  routePreferenceSource: ConfigSource
}

/**
 * Fetch routing config from hierarchy and merge with explicit options.
 * Explicit options take precedence over config values.
 */
async function resolveRoutingOptions(params: {
  customerId: number
  explicitOptions?: RoutingOptions
  useConfigHierarchy: boolean
  executor: GqlExecutor
}): Promise<ResolvedRoutingOptions> {
  const { customerId, explicitOptions = {}, useConfigHierarchy, executor } = params

  // Default values
  let avoidTolls = false
  let avoidTollsSource: ConfigSource = 'hopdrive'
  let avoidHighways = false
  let avoidHighwaysSource: ConfigSource = 'hopdrive'
  let avoidFerries = false
  let avoidFerriesSource: ConfigSource = 'hopdrive'
  let routePreference: 'fastest' | 'shortest' = 'fastest'
  let routePreferenceSource: ConfigSource = 'hopdrive'

  // Fetch from config hierarchy if requested
  if (useConfigHierarchy) {
    try {
      const config = await getCustomerConfig({
        customerId,
        executor,
      })
      const routing = config.routing as Routing | undefined

      if (routing) {
        if (routing.avoid_tolls !== undefined) {
          avoidTolls = routing.avoid_tolls
          avoidTollsSource = 'customer' // From config hierarchy
        }
        if (routing.avoid_highways !== undefined) {
          avoidHighways = routing.avoid_highways
          avoidHighwaysSource = 'customer'
        }
        if (routing.avoid_ferries !== undefined) {
          avoidFerries = routing.avoid_ferries
          avoidFerriesSource = 'customer'
        }
        if (routing.route_preference !== undefined) {
          routePreference = routing.route_preference
          routePreferenceSource = 'customer'
        }
      }
    } catch {
      // If config fetch fails, fall back to defaults
      // This allows enrichment to proceed even if config service is unavailable
      getLogger().warn('enrichLane.configFetchFailed', { customerId, usingDefaults: true })
    }
  }

  // Explicit options override config values
  if (explicitOptions.avoidTolls !== undefined) {
    avoidTolls = explicitOptions.avoidTolls
    avoidTollsSource = 'move' // Explicit override treated as move-level
  }
  if (explicitOptions.avoidHighways !== undefined) {
    avoidHighways = explicitOptions.avoidHighways
    avoidHighwaysSource = 'move'
  }
  if (explicitOptions.avoidFerries !== undefined) {
    avoidFerries = explicitOptions.avoidFerries
    avoidFerriesSource = 'move'
  }
  if (explicitOptions.routePreference !== undefined) {
    routePreference = explicitOptions.routePreference
    routePreferenceSource = 'move'
  }

  return {
    avoidTolls,
    avoidTollsSource,
    avoidHighways,
    avoidHighwaysSource,
    avoidFerries,
    avoidFerriesSource,
    routePreference,
    routePreferenceSource,
  }
}

/**
 * Enrich a lane with route data from geo providers.
 *
 * This function:
 * 1. Fetches the lane with its pickup/delivery locations
 * 2. Resolves routing options (from config hierarchy and/or explicit options)
 * 3. Sets lane status to 'enriching'
 * 4. Computes the route using geo providers
 * 5. Updates the lane with route data, tolls, polyline, and metadata
 * 6. Sets lane status to 'ready' (or 'failed' on error)
 *
 * @example Basic usage
 * ```typescript
 * const result = await enrichLane({ laneId: 123 })
 * console.log(`Distance: ${result.distanceMiles} miles`)
 * console.log(`Duration: ${result.durationSeconds} seconds`)
 * ```
 *
 * @example With config hierarchy (fetches routing preferences from customer config)
 * ```typescript
 * const result = await enrichLane({
 *   laneId: 123,
 *   useConfigHierarchy: true,
 * })
 * // Routing options like avoid_tolls are fetched from the customer's config
 * ```
 *
 * @example With explicit routing options (overrides config)
 * ```typescript
 * const result = await enrichLane({
 *   laneId: 123,
 *   useConfigHierarchy: true,
 *   routingOptions: {
 *     avoidTolls: true, // Overrides config value
 *   },
 * })
 * ```
 *
 * @example With custom geo providers (for testing)
 * ```typescript
 * const result = await enrichLane({
 *   laneId: 123,
 *   geoProviders: mockGeoProviders,
 *   executor: testExecutor,
 * })
 * ```
 */
export async function enrichLane(input: EnrichLaneInput): Promise<EnrichLaneResult> {
  const {
    laneId,
    routingOptions,
    geoProviders,
    useConfigHierarchy = false,
  } = input

  const executor = resolveExecutor(input.executor)
  const providers = geoProviders ?? getDefaultGeoProviders()

  const log = scopeLogger(getLogger(), {
    operationId: generateOperationId(),
    operation: 'enrichLane',
    laneId,
    ...(input.correlationId && { correlationId: input.correlationId }),
  })
  const startedAt = Date.now()

  log.info('enrichLane.start', { laneId, useConfigHierarchy })

  // Step 1: Fetch lane with locations
  const lane = await getLane({
    id: laneId,
    includeLocations: true,
    required: false,
    executor,
  }) as LaneWithLocations | null

  if (!lane) {
    throw new LaneNotFoundError({ id: laneId })
  }

  // Validate locations have coordinates
  const { pickup, delivery } = lane

  if (!pickup.latitude || !pickup.longitude) {
    throw new LaneError(
      `Pickup location ${pickup.id} is missing coordinates. Geocode the location first.`
    )
  }

  if (!delivery.latitude || !delivery.longitude) {
    throw new LaneError(
      `Delivery location ${delivery.id} is missing coordinates. Geocode the location first.`
    )
  }

  // Step 2: Resolve routing options (from config hierarchy and/or explicit options)
  const resolvedOptions = await resolveRoutingOptions({
    customerId: lane.customer_id,
    explicitOptions: routingOptions,
    useConfigHierarchy,
    executor,
  })

  // Step 3: Set status to 'enriching'
  await updateLaneStatus({
    laneId,
    status: 'enriching',
    executor,
  })

  try {
    // Step 4: Build route request
    const routeRequest: ComputeRouteRequest = {
      origin: {
        latitude: pickup.latitude,
        longitude: pickup.longitude,
      },
      destination: {
        latitude: delivery.latitude,
        longitude: delivery.longitude,
      },
      travelMode: 'DRIVE',
      routePreference: toRoutePreference(resolvedOptions.routePreference),
      avoidTolls: resolvedOptions.avoidTolls,
      avoidHighways: resolvedOptions.avoidHighways,
      avoidFerries: resolvedOptions.avoidFerries,
      includePolyline: true,
      includeTolls: !resolvedOptions.avoidTolls, // Only compute tolls if not avoiding
    }

    // Step 5: Compute route
    const routeResult = await providers.routing.computeRoute(routeRequest)

    // Step 6: Build enrichment data
    const distanceMiles = metersToMiles(routeResult.distanceMeters)
    const durationSeconds = routeResult.durationSeconds + PICKUP_INSPECTION_SEC + DELIVERY_INSPECTION_SEC
    const tolls = tollCentsToDollars(routeResult.tolls?.amountCents)
    const polyline = routeResult.polyline ?? null

    log.info('enrichLane.routeComputed', {
      laneId,
      distanceMiles,
      durationSeconds,
      tolls,
      source: routeResult.source,
    });

    const routeMetadata: RouteMetadata = {
      source: routeResult.source,
      computedAt: routeResult.computedAt,
      routeSummary: routeResult.routeSummary,
      warnings: routeResult.warnings,
      staticDurationSeconds: routeResult.staticDurationSeconds,
      hasTrafficData: routeResult.durationSeconds !== routeResult.staticDurationSeconds,
    }

    const creationCriteria: LaneCreationCriteria = {
      avoid_tolls: resolvedOptions.avoidTolls,
      avoid_tolls_source: resolvedOptions.avoidTollsSource,
      route_preference: resolvedOptions.routePreference,
      route_preference_source: resolvedOptions.routePreferenceSource,
      api_version: 'v1',
      created_at: new Date().toISOString(),
    }

    // Step 7: Update lane with enrichment data
    const updateResult = await updateLaneEnrichment({
      laneId,
      distanceMiles,
      durationSeconds,
      tolls,
      polyline,
      routeMetadata,
      enrichmentSource: routeResult.source,
      creationCriteria,
      status: 'ready',
      executor,
    })

    log.info('enrichLane.complete', { laneId, status: 'ready', elapsedMs: elapsed(startedAt) })

    return {
      laneId,
      distanceMiles,
      durationSeconds,
      tolls,
      polyline,
      routeMetadata,
      enrichmentSource: routeResult.source,
      creationCriteria,
      status: 'ready',
      enrichedAt: updateResult.updatedat,
    }
  } catch (error) {
    log.error('enrichLane.failed', error, { laneId, elapsedMs: elapsed(startedAt) })

    // On failure, set status to 'failed'
    await updateLaneStatus({
      laneId,
      status: 'failed',
      executor,
    }).catch(() => {
      log.warn('enrichLane.statusUpdateFailed', { laneId })
    })

    throw error
  }
}
